/**
 * Aufnahme im Browser. Startet, misst den Pegel, stoppt bei Stille.
 *
 * Auf iOS kann MediaRecorder kein webm, deshalb wird der Typ ausgehandelt
 * statt festgelegt. Zwei Sekunden Stille beenden die Aufnahme von selbst:
 * wer im Laden spricht, hat selten eine Hand frei für einen zweiten Tipp.
 */
const STILLE_MS = 2000;
const MAX_MS = 60_000;
const STILLE_SCHWELLE = 0.02;

export interface Aufnahme {
  blob: Blob;
  sekunden: number;
}

export interface AufnahmeSteuerung {
  stop: () => void;
  pegel: () => number;
}

export async function starteAufnahme(fertig: (aufnahme: Aufnahme | null) => void): Promise<AufnahmeSteuerung> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });

  const typ = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((kandidat) =>
    typeof MediaRecorder.isTypeSupported === "function" ? MediaRecorder.isTypeSupported(kandidat) : false,
  );
  const recorder = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined);

  const teile: Blob[] = [];
  const beginn = Date.now();

  const audioContext = new AudioContext();
  const quelle = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 512;
  quelle.connect(analyser);
  const puffer = new Uint8Array(analyser.frequencyBinCount);

  let letzterTon = Date.now();
  let aktuellerPegel = 0;

  const messen = window.setInterval(() => {
    analyser.getByteTimeDomainData(puffer);
    let summe = 0;
    for (const wert of puffer) summe += ((wert - 128) / 128) ** 2;
    aktuellerPegel = Math.sqrt(summe / puffer.length);

    if (aktuellerPegel > STILLE_SCHWELLE) letzterTon = Date.now();
    if (Date.now() - letzterTon > STILLE_MS || Date.now() - beginn > MAX_MS) stop();
  }, 100);

  function aufraeumen() {
    window.clearInterval(messen);
    for (const spur of stream.getTracks()) spur.stop();
    void audioContext.close();
  }

  recorder.addEventListener("dataavailable", (event) => {
    if (event.data.size > 0) teile.push(event.data);
  });

  recorder.addEventListener("stop", () => {
    aufraeumen();
    const sekunden = (Date.now() - beginn) / 1000;
    // Sehr kurze Aufnahmen sind fast immer ein versehentlicher Tipp.
    fertig(sekunden < 0.6 || teile.length === 0 ? null : { blob: new Blob(teile, { type: recorder.mimeType }), sekunden });
  });

  recorder.start();

  function stop() {
    if (recorder.state !== "inactive") recorder.stop();
  }

  return { stop, pegel: () => aktuellerPegel };
}
